import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap"
import { Asset, AssetId, GoalAssetCoordinates, GoalId, SavingsSummary } from "./model"
import { CurrencyAmount } from "../model/CurrencyAmount"
import { useEffect, useState } from "react"
import { AmountComponent } from "../AmountComponent"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRight, faBan } from "@fortawesome/free-solid-svg-icons"
import _ from "lodash"

interface TransferModalProps {
  source: GoalAssetCoordinates | null
  target: GoalAssetCoordinates | null
  summary: SavingsSummary
  onHide: () => void
  onTransfer: (amount: number) => void
}

export const TransferModal: React.FC<TransferModalProps> = ({ source, target, onHide, summary, onTransfer }) => {
  const extractAssetName = (asset: Asset) => {
    if ("managedSavingsAccount" in asset) {
      return asset.managedSavingsAccount.name
    } else {
      return asset.custom.name
    }
  }

  const extractAssetCurrency = (asset: Asset) => {
    if ("managedSavingsAccount" in asset) {
      return asset.managedSavingsAccount.balance.currency
    } else {
      return asset.custom.balance.currency
    }
  }

  const findAmount = (assetId: AssetId, goalId: GoalId): CurrencyAmount | null => {
    return summary.mapping[goalId]?.[assetId] ?? (summary.assets[assetId] && { amount: 0, currency: extractAssetCurrency(summary.assets[assetId]) })
  }

  const sourceCurrencyAmount: CurrencyAmount | null = source && findAmount(source.assetId, source.goalId)
  const targetCurrencyAmount: CurrencyAmount | null = target && findAmount(target.assetId, target.goalId)

  const commonCurrency =
    (sourceCurrencyAmount && targetCurrencyAmount && sourceCurrencyAmount.currency === targetCurrencyAmount.currency) ? sourceCurrencyAmount.currency :
      (sourceCurrencyAmount && !targetCurrencyAmount) ? sourceCurrencyAmount.currency :
        (targetCurrencyAmount && !sourceCurrencyAmount) ? targetCurrencyAmount.currency :
          null



  const [transferAmount, setTransferAmount] = useState<number>(sourceCurrencyAmount?.amount || targetCurrencyAmount?.amount || 0)

  const sourceAmountAfterTransfer = (sourceCurrencyAmount?.amount ?? 0) - transferAmount

  const targetAmountAfterTransfer = (targetCurrencyAmount?.amount ?? 0) + transferAmount

  useEffect(() => setTransferAmount(sourceCurrencyAmount?.amount || 0), [sourceCurrencyAmount, source, target])

  return <Modal show={(source !== null) && (target !== null) && !_.isEqual(source, target)} onHide={onHide}>
    <Modal.Header closeButton>
      <Modal.Title>Transfer</Modal.Title>
    </Modal.Header>
    {
      (source && target) &&
      <Modal.Body>
        {
          commonCurrency &&
          <Form onSubmit={(e) => { e.preventDefault(); if (transferAmount > 0 && commonCurrency) { onTransfer(transferAmount) } }}>
            <div className="d-flex flex-row align-items-center mb-3">
              <Card>
                <Card.Body>
                  <Card.Text className="d-flex flex-column">
                    <span>{extractAssetName(summary.assets[source.assetId])}</span>
                    <span>{summary.goals[source.goalId].name}</span>
                    <span><AmountComponent amount={sourceAmountAfterTransfer} currency={commonCurrency} /></span>
                  </Card.Text>
                </Card.Body>
              </Card>
              <FontAwesomeIcon icon={faArrowRight} className="mx-3" />
              <Card>
                <Card.Body>
                  <Card.Text className="d-flex flex-column">
                    <span>{extractAssetName(summary.assets[target.assetId])}</span>
                    <span>{summary.goals[target.goalId].name}</span>
                    <span><AmountComponent amount={targetAmountAfterTransfer} currency={commonCurrency} /></span>
                  </Card.Text>
                </Card.Body>
              </Card>

            </div>
            <Form.Group as={Row} className="mb-3" controlId="modal-transfer-amount">
              <Form.Label column sm="2">Amount</Form.Label>
              <Col sm="10">
                <Form.Control type="text" autoFocus onFocus={(e) => e.target.select()} defaultValue={transferAmount} onChange={(e) => setTransferAmount(Number(e.target.value.replaceAll(",", ".").replaceAll(/[^0-9.]/g, "")) > 0 ? Number(e.target.value.replaceAll(",", ".").replaceAll(/[^0-9.]/g, "")) : 0)} isInvalid={transferAmount === 0} />
              </Col>
            </Form.Group>
          </Form>
        }
      </Modal.Body>
    }

    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}><FontAwesomeIcon icon={faBan} /> Cancel</Button>
      <Button variant="primary" disabled={!commonCurrency || !(transferAmount > 0)} onClick={() => onTransfer(transferAmount)}><FontAwesomeIcon icon={faArrowRight} /> Transfer</Button>
    </Modal.Footer>
  </Modal >
}
